/**
 * 抽查任务列表
 *
 * 任务来源与入池原因（source / source_detail）为生成时的快照，事后不可回算，
 * 是审计与处罚依据链的一环，列表需完整展示。
 */
import React from 'react';
import {Button, Table, Tag} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import {
    ExemptTargetType,
    SpotTaskConclusion,
    SpotTaskRow,
    SpotTaskSource,
    SpotTaskStatus,
    SpotTaskTab,
} from '../types/ExemptSpotCheckType';
import {CONCLUSION_TAG_COLOR} from '../config/exemptSpotCheckConfig';
import {formatCount} from '../utils/metricFormat';

interface SpotTaskTableProps {
    rows: SpotTaskRow[];
    loading: boolean;
    /** 当前分组：待抽查分组不展示抽查人 / 时间 / 结果三列，它们都要提交结论后才有值 */
    tab: SpotTaskTab;
    onOpenDetail: (row: SpotTaskRow) => void;
}

const SpotTaskTable: React.FC<SpotTaskTableProps> = ({rows, loading, tab, onOpenDetail}) => {
    const {t} = useTranslation();

    /** 围栏地址数：物化挂载完成前 addressCount 为空，展示「统计中」 */
    const renderFenceSuffix = (row: SpotTaskRow): string =>
        String(
            t('exemptSpotCheck.monitor.fenceAddressCount', {
                addressCount: row.addressCount ?? String(t('exemptSpotCheck.monitor.counting')),
            }),
        );

    /**
     * 操作列只有一个入口：未完成→「开始抽查」，已完成→「查看」。
     * 无「领取」动作 —— 抽查人不预先分配，谁提交结论就记谁。
     */
    const renderAction = (row: SpotTaskRow) => (
        <Button type="link" size="small" className="px-0" onClick={() => onOpenDetail(row)}>
            {row.status === SpotTaskStatus.DONE
                ? t('exemptSpotCheck.task.view')
                : t('exemptSpotCheck.task.startCheck')}
        </Button>
    );

    /** 仅已完成的分组展示：三者都在提交结论时才写入，待抽查分组下整列为空 */
    const doneColumns: ColumnsType<SpotTaskRow> = [
        {
            title: t('exemptSpotCheck.task.columns.assignee'),
            dataIndex: 'assigneeName',
            width: 150,
            align: 'left' as const,
            render: (name?: string) => name ?? <span className="text-gray-300">-</span>,
        },
        {
            title: t('exemptSpotCheck.task.columns.doneAt'),
            dataIndex: 'doneAt',
            width: 150,
            align: 'left' as const,
            // 后端给的是 UTC ISO 原文，按浏览器本地时区展示，与质控模块其它页面口径一致
            render: (value?: string) =>
                value ? new Date(value).toLocaleString() : <span className="text-gray-300">-</span>,
        },
        {
            title: t('exemptSpotCheck.task.columns.conclusion'),
            dataIndex: 'conclusion',
            width: 240,
            align: 'left' as const,
            render: (conclusion: SpotTaskConclusion | undefined, row) => {
                if (!conclusion) {
                    return <Tag>{t('exemptSpotCheck.task.pendingCheck')}</Tag>;
                }
                return (
                    <div>
                        <Tag color={CONCLUSION_TAG_COLOR[conclusion]}>
                            {t(`exemptSpotCheck.conclusion.${conclusion}`)}
                        </Tag>
                        {row.conclusionRemark && (
                            <div className="mt-1 text-xs text-gray-500">{row.conclusionRemark}</div>
                        )}
                    </div>
                );
            },
        },
    ];

    const columns: ColumnsType<SpotTaskRow> = [
        {
            title: t('exemptSpotCheck.task.columns.taskNo'),
            dataIndex: 'taskNo',
            width: 100,
            align: 'left' as const,
            fixed: 'left',
            render: (taskNo: string) => <span className="text-purple-600">{taskNo}</span>,
        },
        {
            title: t('exemptSpotCheck.task.columns.targetNo'),
            dataIndex: 'targetNo',
            width: 100,
            align: 'left' as const,
        },
        {
            title: t('exemptSpotCheck.task.columns.address'),
            dataIndex: 'address',
            width: 240,
            align: 'left' as const,
            render: (address: string, row) => (
                <div>
                    <div className="text-gray-800">
                        {address}
                        {row.targetType === ExemptTargetType.FENCE && (
                            <span className="ml-1 text-gray-500">{renderFenceSuffix(row)}</span>
                        )}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-400">{row.zipcode}</div>
                </div>
            ),
        },
        {
            title: t('exemptSpotCheck.task.columns.source'),
            dataIndex: 'source',
            width: 220,
            align: 'left' as const,
            render: (source: SpotTaskSource, row) => (
                <div>
                    <div className="text-gray-800">{t(`exemptSpotCheck.taskSource.${source}`)}</div>
                    {row.sourceDetail && (
                        <div className="mt-1 text-xs text-gray-500">{row.sourceDetail}</div>
                    )}
                </div>
            ),
        },
        {
            // 半月粒度（2026-P17）；表有 period_type，日后改粒度不需数据迁移
            title: t('exemptSpotCheck.task.columns.periodNo'),
            dataIndex: 'periodNo',
            width: 100,
            align: 'left' as const,
        },
        {
            title: t('exemptSpotCheck.task.columns.exemptCount'),
            dataIndex: 'exemptCount',
            width: 100,
            align: 'right' as const,
            render: (value?: number) => formatCount(value, String(t('exemptSpotCheck.unit.order'))),
        },
        ...(tab === SpotTaskTab.PENDING ? [] : doneColumns),
        {
            title: t('exemptSpotCheck.columns.action'),
            key: 'action',
            width: 110,
            align: 'left' as const,
            fixed: 'right',
            render: (_, row) => renderAction(row),
        },
    ];

    return (
        <Table<SpotTaskRow>
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={false}
            scroll={{x: tab === SpotTaskTab.PENDING ? 900 : 1400}}
            size="small"
        />
    );
};

export default SpotTaskTable;
